<template>
  <div class="financial-edit">
    <div class="page-header">
      <h2>{{ isEdit ? '编辑财务记录' : '新增财务记录' }}</h2>
      <div class="header-actions">
        <a-button @click="handleBack">
          <template #icon>
            <ArrowLeftOutlined />
          </template>
          返回
        </a-button>
      </div>
    </div>
    
    <a-card>
      <a-form
        ref="formRef"
        :model="formData"
        :rules="rules"
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 18 }"
        @finish="handleSubmit"
      >
        <a-row :gutter="24">
          <a-col :span="12">
            <a-form-item label="记录日期" name="recordDate" required>
              <a-date-picker
                v-model:value="formData.recordDate"
                format="YYYY-MM-DD"
                value-format="YYYYMMDD"
                style="width: 100%"
                placeholder="请选择记录日期"
              />
            </a-form-item>
          </a-col>
          
          <a-col :span="12">
            <a-form-item label="经办人" name="operatorName" required>
              <a-input
                v-model:value="formData.operatorName"
                placeholder="请输入经办人姓名"
                maxlength="50"
              />
            </a-form-item>
          </a-col>
          
          <a-col :span="12">
            <a-form-item label="收支类型" name="transactionType" required>
              <a-radio-group v-model:value="formData.transactionType">
                <a-radio value="income">收入</a-radio>
                <a-radio value="expense">支出</a-radio>
              </a-radio-group>
            </a-form-item>
          </a-col>
          
          <a-col :span="12">
            <a-form-item label="财务分类" name="categoryId" required>
              <a-select
                v-model:value="formData.categoryId"
                placeholder="请选择财务分类"
                @focus="loadCategories"
              >
                <a-select-option
                  v-for="category in categories"
                  :key="category.id"
                  :value="category.id"
                >
                  {{ category.name }}
                </a-select-option>
              </a-select>
            </a-form-item>
          </a-col>
          
          <a-col :span="12">
            <a-form-item label="金额" name="amount" required>
              <a-input-number
                v-model:value="formData.amount"
                :precision="2"
                :min="0"
                style="width: 100%"
                placeholder="请输入金额"
                addon-before="￥"
              />
            </a-form-item>
          </a-col>
          
          <a-col :span="12">
            <a-form-item label="财务账户" name="financialAccount">
              <a-input
                v-model:value="formData.financialAccount"
                placeholder="请输入财务账户"
                maxlength="100"
              />
            </a-form-item>
          </a-col>
          
          <a-col :span="24">
            <a-form-item label="备注" name="remark" :label-col="{ span: 3 }" :wrapper-col="{ span: 21 }">
              <a-textarea
                v-model:value="formData.remark"
                placeholder="请输入备注信息"
                :rows="4"
                maxlength="500"
                show-count
              />
            </a-form-item>
          </a-col>
        </a-row>
        
        <a-form-item :wrapper-col="{ offset: 3, span: 21 }">
          <a-space>
            <a-button type="primary" html-type="submit" :loading="saving">
              <template #icon>
                <SaveOutlined />
              </template>
              {{ isEdit ? '更新' : '保存' }}
            </a-button>
            <a-button @click="handleReset">
              重置
            </a-button>
            <a-button @click="handleBack">
              取消
            </a-button>
          </a-space>
        </a-form-item>
      </a-form>
    </a-card>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { message } from 'ant-design-vue'
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons-vue'
import { financialApi } from '@/api/financial'
import { financialCategoryApi } from '@/api/financialCategory'

export default {
  name: 'FinancialEdit',
  components: {
    ArrowLeftOutlined,
    SaveOutlined
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const formRef = ref()
    const saving = ref(false)
    const categories = ref([])
    
    const isEdit = computed(() => !!route.params.id)
    const recordId = computed(() => route.params.id)
    
    const formData = reactive({
      recordDate: undefined,
      operatorName: '',
      categoryId: undefined,
      transactionType: 'income',
      amount: null,
      financialAccount: '',
      remark: ''
    })
    
    const rules = {
      recordDate: [
        { required: true, message: '请选择记录日期' }
      ],
      operatorName: [
        { required: true, message: '请输入经办人姓名' },
        { max: 50, message: '经办人姓名不能超过50个字符' }
      ],
      transactionType: [
        { required: true, message: '请选择收支类型' }
      ],
      categoryId: [
        { required: true, message: '请选择财务分类' }
      ],
      amount: [
        { required: true, message: '请输入金额' },
        { 
          validator: (rule, value) => {
            if (value === undefined || value === null || value === '') {
              return Promise.resolve() // 让 required 规则处理空值
            }
            const num = Number(value)
            if (isNaN(num)) {
              return Promise.reject('请输入有效的金额')
            }
            if (num < 0) {
              return Promise.reject('金额不能小于0')
            }
            return Promise.resolve()
          }
        }
      ],
      financialAccount: [
        { max: 100, message: '财务账户不能超过100个字符' }
      ],
      remark: [
        { max: 500, message: '备注不能超过500个字符' }
      ]
    }
    
    const loadCategories = async () => {
      if (categories.value.length > 0) return
      
      try {
        const result = await financialCategoryApi.getTree()
        console.log('财务分类数据:', result.data) // 添加调试日志
        const flatCategories = []
        
        const flattenCategories = (cats, prefix = '') => {
          if (!cats || !Array.isArray(cats)) return
          
          cats.forEach(cat => {
            if (cat.status === 1 || cat.status === '1') { // 兼容数字和字符串类型
              flatCategories.push({
                id: cat.id,
                name: prefix + cat.name
              })
              if (cat.children && cat.children.length > 0) {
                flattenCategories(cat.children, prefix + cat.name + ' / ')
              }
            }
          })
        }
        
        flattenCategories(result.data)
        categories.value = flatCategories
        console.log('扁平化后的分类数据:', flatCategories) // 添加调试日志
      } catch (error) {
        console.error('加载财务分类失败:', error)
        message.error('加载财务分类失败')
      }
    }
    
    const loadRecordDetail = async () => {
      if (!isEdit.value) return
      
      try {
        const result = await financialApi.detail(recordId.value)
        const data = result.data
        console.log('从接口获取的详情数据:', data) // 添加调试日志
        
        Object.assign(formData, {
          recordDate: data.recordDate,
          operatorName: data.operatorName,
          categoryId: data.categoryId,
          transactionType: data.transactionType,
          amount: Number(data.amount), // 确保转换为数字类型
          financialAccount: data.financialAccount || '',
          remark: data.remark || ''
        })
        
        console.log('填充后的表单数据:', formData) // 添加调试日志
      } catch (error) {
        console.error('加载记录详情失败:', error)
        message.error('加载记录详情失败')
        handleBack()
      }
    }
    
    const handleSubmit = async () => {
      try {
        await formRef.value.validate()
        
        saving.value = true
        const submitData = {
          ...formData
        }
        
        if (isEdit.value) {
          submitData.id = recordId.value
          console.log('编辑模式，提交数据:', submitData) // 添加调试日志
        } else {
          console.log('新增模式，提交数据:', submitData) // 添加调试日志
        }
        
        await financialApi.edit(submitData)
        message.success(isEdit.value ? '更新成功' : '保存成功')
        handleBack()
      } catch (error) {
        if (error.errorFields) {
          // 表单验证错误
          return
        }
        console.error('保存失败:', error)
        message.error(`保存失败: ${error.message || '未知错误'}`)
      } finally {
        saving.value = false
      }
    }
    
    const handleReset = () => {
      formRef.value.resetFields()
    }
    
    const handleBack = () => {
      router.push('/financial/records')
    }
    
    onMounted(async () => {
      await loadCategories()
      await loadRecordDetail()
    })
    
    return {
      formRef,
      saving,
      categories,
      isEdit,
      formData,
      rules,
      handleSubmit,
      handleReset,
      handleBack,
      loadCategories
    }
  }
}
</script>

<style scoped>
.financial-edit {
  padding: 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-header h2 {
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 8px;
}
</style>